
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { SEO } from '../../utils/seo';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy - LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS",
    "description": "Privacy Policy for LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS. Learn how we collect, use, and protect your personal information.",
    "url": `${window.location.origin}/privacy-policy`
  };

  return (
    <>
      <SEO
        title="Privacy Policy | LEWIS SECURITY North Wales"
        description="Privacy Policy for LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS. Learn how we collect, use, and protect your personal information in compliance with UK GDPR regulations."
        keywords="privacy policy, data protection, GDPR, personal information, security compliance"
        schema={schema}
        canonical={`${window.location.origin}/privacy-policy`}
      />
      <div className="min-h-screen bg-white font-inter">
        <Navbar />
        
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Personal Information</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may collect personal information that you voluntarily provide to us when you:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Contact us through our website forms</li>
                  <li>Request a quote or consultation</li>
                  <li>Subscribe to our newsletter</li>
                  <li>Register for training courses</li>
                  <li>Engage our services</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This information may include:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Name and contact details (email address, phone number)</li>
                  <li>Company name and business information</li>
                  <li>Service requirements and preferences</li>
                  <li>Any other information you choose to provide</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Automatically Collected Information</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  When you visit our website, we may automatically collect certain information about your device, including:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>IP address and browser type</li>
                  <li>Operating system and device information</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Referring website addresses</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use the information we collect for the following purposes:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>To respond to your inquiries and provide customer support</li>
                  <li>To deliver our security, risk assessment, and compliance services</li>
                  <li>To process training course registrations</li>
                  <li>To send you relevant information about our services</li>
                  <li>To improve our website and services</li>
                  <li>To comply with legal obligations</li>
                  <li>To protect our business interests and enforce our terms</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Legal Basis for Processing</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Under UK GDPR, we process your personal data based on:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li><strong>Consent:</strong> When you provide explicit consent for specific purposes</li>
                  <li><strong>Contract:</strong> When processing is necessary to fulfill our contractual obligations</li>
                  <li><strong>Legal Obligation:</strong> When we must comply with legal requirements</li>
                  <li><strong>Legitimate Interests:</strong> When processing is necessary for our legitimate business interests</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Information Sharing and Disclosure</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share your information with:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li><strong>Service Providers:</strong> Trusted third parties who assist in operating our website and conducting our business</li>
                  <li><strong>Professional Advisors:</strong> Lawyers, accountants, and other professional advisors</li>
                  <li><strong>Regulatory Authorities:</strong> When required by law or to protect our legal rights</li>
                  <li><strong>Business Transfers:</strong> In connection with any merger, sale, or acquisition of our business</li>
                </ul>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Security</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When your information is no longer needed, we will securely delete or anonymize it.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Your Rights</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Under UK GDPR, you have the following rights:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li><strong>Right to Access:</strong> Request copies of your personal data</li>
                  <li><strong>Right to Rectification:</strong> Request correction of inaccurate information</li>
                  <li><strong>Right to Erasure:</strong> Request deletion of your personal data</li>
                  <li><strong>Right to Restrict Processing:</strong> Request limitation of how we use your data</li>
                  <li><strong>Right to Data Portability:</strong> Request transfer of your data to another organization</li>
                  <li><strong>Right to Object:</strong> Object to processing of your personal data</li>
                  <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time where we rely on consent</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  To exercise any of these rights, please contact us at <a href="mailto:info@jl-security.co.uk" className="text-teal-600 hover:text-teal-700 font-medium">info@jl-security.co.uk</a>
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Cookies</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our website may use cookies to enhance your browsing experience. Cookies are small text files stored on your device. You can control cookie settings through your browser preferences.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Third-Party Links</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Children's Privacy</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have any questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 mb-2"><strong>LEWIS SECURITY, RISK & COMPLIANCE SOLUTIONS</strong></p>
                  <p className="text-gray-700 mb-2">Email: <a href="mailto:info@jl-security.co.uk" className="text-teal-600 hover:text-teal-700 font-medium">info@jl-security.co.uk</a></p>
                  <p className="text-gray-700">Phone: <a href="tel:+447834561015" className="text-teal-600 hover:text-teal-700 font-medium">+44 7834 561015</a></p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Complaints</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you believe we have not handled your personal data properly, you have the right to lodge a complaint with the Information Commissioner's Office (ICO):
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 mb-2"><strong>Information Commissioner's Office</strong></p>
                  <p className="text-gray-700 mb-2">Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 font-medium">www.ico.org.uk</a></p>
                  <p className="text-gray-700">Helpline: 0303 123 1113</p>
                </div>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <Link 
                to="/" 
                className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
